// Date helpers. All calendar maths is done in the device's local time zone
// (the household is in the UK), with ISO YYYY-MM-DD strings for dates and
// epoch milliseconds for instants.

import type { ISODate, Millis } from './types.ts';

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

export function toISODate(d: Date | Millis): ISODate {
  const date = typeof d === 'number' ? new Date(d) : d;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse YYYY-MM-DD as local midnight. */
export function parseISODate(s: ISODate): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function startOfDay(t: Millis): Millis {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function addDays(date: ISODate, days: number): ISODate {
  const d = parseISODate(date);
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

export function addMonths(date: ISODate, months: number): ISODate {
  const d = parseISODate(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return toISODate(d);
}

/** Whole calendar days from a to b (b - a). */
export function daysBetween(a: ISODate, b: ISODate): number {
  const ms = parseISODate(b).getTime() - parseISODate(a).getTime();
  return Math.round(ms / DAY);
}

export function today(now: Millis = Date.now()): ISODate {
  return toISODate(now);
}

/** "HH:MM" → minutes after midnight. */
export function clockToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Minutes after local midnight for an instant. */
export function minutesOfDay(t: Millis): number {
  const d = new Date(t);
  return d.getHours() * 60 + d.getMinutes();
}

/** Instant for a HH:MM clock time on the same local day as `t` (DST-safe). */
export function atClock(t: Millis, hhmm: string): Millis {
  const d = new Date(t);
  const [h, m] = hhmm.split(':').map(Number);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), h ?? 0, m ?? 0).getTime();
}

/** Local midnight `n` calendar days after the day containing `t` (DST-safe). */
export function dayStart(t: Millis, n = 0): Millis {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n).getTime();
}

/** Is `t` within a window that may wrap midnight (e.g. 21:30 → 06:30)? */
export function inClockWindow(t: Millis, start: string, end: string): boolean {
  const m = minutesOfDay(t);
  const s = clockToMinutes(start);
  const e = clockToMinutes(end);
  return s <= e ? m >= s && m < e : m >= s || m < e;
}

/** Compact duration: "45m", "2h 5m", "3d". */
export function formatDuration(ms: Millis, opts: { long?: boolean } = {}): string {
  const neg = ms < 0;
  const mins = Math.round(Math.abs(ms) / MINUTE);
  let out: string;
  if (mins < 60) out = opts.long ? `${mins} min` : `${mins}m`;
  else if (mins < 60 * 48) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    out = m ? `${h}h ${m}m` : `${h}h`;
  } else out = `${Math.round(mins / 1440)}d`;
  return neg ? `-${out}` : out;
}

export function formatTime(t: Millis): string {
  return new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function formatDate(d: ISODate | Millis, style: 'short' | 'long' | 'weekday' = 'short'): string {
  const date = typeof d === 'number' ? new Date(d) : parseISODate(d);
  if (style === 'long') return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  if (style === 'weekday') return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** "today", "tomorrow", "in 5 days", "3 days ago". */
export function relativeDay(date: ISODate, now: Millis = Date.now()): string {
  const n = daysBetween(today(now), date);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n === -1) return 'yesterday';
  if (n > 0) return n < 14 ? `in ${n} days` : n < 60 ? `in ${Math.round(n / 7)} weeks` : `in ${Math.round(n / 30)} months`;
  const a = -n;
  return a < 14 ? `${a} days ago` : a < 60 ? `${Math.round(a / 7)} weeks ago` : `${Math.round(a / 30)} months ago`;
}

export function ago(t: Millis, now: Millis = Date.now()): string {
  const d = now - t;
  if (d < MINUTE) return 'just now';
  return `${formatDuration(d)} ago`;
}
